/**
 * Disk, retention, and the sweep.
 *
 * Retention runs on its own every few hours; this page exists for the two moments it is not
 * enough — when you want the space back now, and when you want to know which client is using it.
 */
import { useState } from 'react';
import { ApiError, api, type Storage } from '../api';

function gb(bytes: number): string {
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

export function StorageView({ storage, onToast, onChanged }: {
  storage: Storage | null;
  onToast: (message: string) => void;
  onChanged: () => void;
}) {
  const [sweeping, setSweeping] = useState(false);

  if (!storage) return <div className="empty"><h1>Reading the disk</h1></div>;

  const disk = storage.disk;
  const total = storage.byClient.reduce((sum, entry) => sum + entry.bytes, 0);

  return (
    <div className="inner">
      <header className="run-head">
        <div className="run-title"><h1>Storage</h1></div>
        <dl className="facts">
          {disk && (
            <>
              <div className="fact">
                <dt>Free</dt>
                <dd>{gb(disk.freeBytes)}</dd>
              </div>
              <div className="fact">
                <dt>Of</dt>
                <dd>{gb(disk.totalBytes)}</dd>
              </div>
            </>
          )}
          <div className="fact">
            <dt>Builds on disk</dt>
            <dd>{gb(total)}</dd>
          </div>
          <div className="fact">
            <dt>Kept per client</dt>
            <dd>{storage.keepPerClient}</dd>
          </div>
        </dl>

        {disk && (
          <div className="gauge" role="img"
               aria-label={`${Math.round(disk.usedFraction * 100)} percent of the disk is in use`}>
            <span className={disk.belowFloor ? 'gauge-fill low' : 'gauge-fill'}
                  style={{ width: `${Math.min(100, Math.round(disk.usedFraction * 100))}%` }} />
            <span className="gauge-label">{Math.round(disk.usedFraction * 100)}% in use</span>
          </div>
        )}

        <p style={{ color: 'var(--ink-2)', maxWidth: '64ch', margin: '18px 0 0' }}>
          Older than the newest {storage.keepPerClient} per client are deleted every few hours.
          The newest passing release is always kept.
        </p>

        <div className="actions" style={{ marginTop: 18 }}>
          <button
            type="button"
            className="btn quiet"
            disabled={sweeping}
            onClick={() => {
              setSweeping(true);
              api.sweepStorage()
                .then((result) => {
                  onToast(result.removed
                    ? `Removed ${result.removed} release${result.removed === 1 ? '' : 's'}, freed ${gb(result.bytesFreed)}`
                    : 'Nothing old enough to remove');
                  onChanged();
                })
                .catch((failure: ApiError) => onToast(failure.message))
                .finally(() => setSweeping(false));
            }}
          >
            {sweeping ? 'Sweeping' : 'Run retention now'}
          </button>
        </div>
      </header>

      {storage.queuePaused && (
        <div className="failure" style={{ borderLeftColor: 'var(--fail)' }}>
          <h3>The queue is paused</h3>
          <pre>
            Free space is below the floor. Remove some releases and builds resume on their own.
          </pre>
        </div>
      )}

      <section className="section">
        <div className="section-head">
          <h2>By client</h2>
          <span className="note">{storage.byClient.length} with files on disk</span>
        </div>
        {storage.byClient.length === 0 ? (
          <p style={{ color: 'var(--ink-3)' }}>Nothing on disk yet.</p>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr><th>Client</th><th>Releases</th><th>Size</th></tr>
              </thead>
              <tbody>
                {storage.byClient.map((entry) => (
                  <tr key={entry.slug}>
                    <td>{entry.slug}</td>
                    <td className="num">{entry.releases}</td>
                    <td className="num">{gb(entry.bytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {storage.orphans.length > 0 && (
        <section className="section">
          <div className="section-head">
            <h2>Unclaimed directories</h2>
            <span className="note">not deleted automatically</span>
          </div>
          <p style={{ color: 'var(--ink-2)', maxWidth: '64ch' }}>
            Release folders with no build behind them. Nothing here removes them.
          </p>
          <div className="files">
            {storage.orphans.map((orphan) => (
              <div className="file" key={orphan.dir}>
                <span className="file-name">{orphan.dir}</span>
                <span className="file-what" />
                <span className="file-size">{gb(orphan.bytes)}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
